/**
 * Detects iOS/iPadOS devices.
 *
 * Uses navigator.userAgentData (modern, Chrome/Edge) when available,
 * falls back to userAgent string + navigator.platform (Safari/older browsers).
 * The platform fallback is needed because iPadOS reports as "MacIntel" in the UA
 * string but has touch support — and Safari doesn't implement userAgentData.
 */
function isIOS(): boolean {
    if (typeof window === 'undefined') return false

    // Modern: navigator.userAgentData (not supported in Safari as of 2026)
    const uaData = (navigator as any).userAgentData
    if (uaData?.platform) {
        return /iOS|iPadOS/i.test(uaData.platform)
    }

    // Legacy: UA string covers iPhone/iPad/iPod directly
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) return true

    // iPadOS detection: reports as MacIntel but has touch
    // eslint-disable-next-line deprecation/deprecation
    if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true

    return false
}

/** Returns true when running as an installed PWA (home screen app) */
function isStandalone(): boolean {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true
}

/**
 * Returns true if the current device is iOS AND the app is NOT running
 * in standalone (PWA/home screen) mode.
 *
 * Web push notifications on iOS require standalone mode — this function
 * identifies when the user needs to "Add to Home Screen" before push can work.
 */
export function isIOSNonStandalone(): boolean {
    return isIOS() && !isStandalone()
}

/**
 * Returns true if the current device is iOS AND the app IS running
 * in standalone (PWA/home screen) mode.
 */
export function isIOSStandalone(): boolean {
    return isIOS() && isStandalone()
}
